const API_URL = import.meta.env.VITE_API_URL ?? '/api/v1'

type ApiError = Error & { status?: number }

export class ApiClient {
  private token = localStorage.getItem('swiss_mail_token') ?? undefined
  private role = localStorage.getItem('swiss_mail_role') ?? undefined
  private email = localStorage.getItem('swiss_mail_email') ?? undefined

  setToken(token?: string) {
    this.token = token
    if (token) localStorage.setItem('swiss_mail_token', token)
    else {
      localStorage.removeItem('swiss_mail_token')
      this.role = undefined
      this.email = undefined
      localStorage.removeItem('swiss_mail_role')
      localStorage.removeItem('swiss_mail_email')
    }
  }

  setSession(token: string, role: string, email?: string) {
    this.setToken(token)
    this.role = role
    localStorage.setItem('swiss_mail_role', role)
    if (email) {
      this.email = email
      localStorage.setItem('swiss_mail_email', email)
    }
  }

  isAuthenticated() { return Boolean(this.token) }
  canAccess(audience: 'admin' | 'user') { return this.isAuthenticated() && (audience === 'user' || this.role === 'admin') }
  getEmail() { return this.email }
  getRole() { return this.role }

  async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const headers = new Headers(init.headers)
    headers.set('Accept', 'application/json')
    if (init.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json')
    if (this.token) headers.set('Authorization', `Bearer ${this.token}`)
    const response = await fetch(`${API_URL}${path}`, { ...init, headers, credentials: 'include' })
    if (!response.ok) {
      if (response.status === 401 && this.token) {
        this.setToken()
        const currentPath = window.location.pathname
        const loginPath = currentPath.startsWith('/admin') ? '/admin/login' : '/user/login'
        window.location.href = loginPath
      }
      const data = await response.json().catch(() => null) as { error?: string } | null
      const error: ApiError = new Error(data?.error ?? `Request failed: ${response.status}`)
      error.status = response.status
      throw error
    }
    return response.status === 204 ? undefined as T : response.json() as Promise<T>
  }

  get<T>(path: string) { return this.request<T>(path) }
  post<T>(path: string, body?: unknown) { return this.request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) }) }
  patch<T>(path: string, body: unknown) { return this.request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }) }
  delete<T>(path: string) { return this.request<T>(path, { method: 'DELETE' }) }
  login(identity: string, password: string) { return this.post<{ accessToken: string; user: { role: string; email: string } }>('/auth/login', { identity, password }) }
  logout() { return this.post<void>('/auth/logout').finally(() => this.setToken()) }
}

export const api = new ApiClient()
